import { MAP_CATALOG } from '../map/mapData.ts';
import { MapId } from '../domain/types.ts';

export interface GeneratedConditions {
  locationName: string;
  region: 'north' | 'central' | 'south';
  density: number; // 0.1 -> 1.0 (mật độ người đi bộ & walker)
  initialParanoia: number; // 0 -> 100 (% cảnh giác xóm)
  neighborhoodType: string; // "Ngõ Bàn Cờ Cổ", "Hẻm Nhậu Sài Gòn", "Phố Đi Bộ Ven Biển", v.v.
  landmarks: Array<{
    type: 'tea_stall' | 'camera' | 'guard' | 'waste_basket';
    name: string;
  }>;
  obstacles: string[];
  gameTheoryNote: string;
  isAiGenerated: boolean;
}

export class AiMapGenerator {
  private static apiKey =
    (typeof import.meta !== 'undefined' &&
      (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_GEMINI_API_KEY) ||
    'AQ.Ab8RN6J7DefWeZwo2plcX50lfowUF5584uhU_xznmOmgcOQSpg';

  /**
   * Sinh các điều kiện ban đầu dựa vào Tên địa điểm & Vùng miền
   * Tự động Fallback sang procedural template khi lỗi mạng hoặc hết quota
   */
  public static async generateInitialConditions(
    locationName: string,
    region: 'north' | 'central' | 'south' = 'north'
  ): Promise<GeneratedConditions> {
    try {
      const prompt = `Bạn là Game Designer của trò chơi "The Dog Poop Dilemma (Spatial Game Theory Vỉa Hè)".
Hãy phân tích địa danh "${locationName}" (Vùng: ${region}) và tạo điều kiện ban đầu cho màn chơi dưới dạng JSON hợp lệ duy nhất:
{
  "locationName": "${locationName.split(',')[0]}",
  "density": 0.7,
  "initialParanoia": 25,
  "neighborhoodType": "Ngõ ngách dân cư đặc thù",
  "landmarks": [
    {"type": "tea_stall", "name": "Quán Nước Cóc Vỉa Hè"},
    {"type": "camera", "name": "Camera Giám Sát Cửa Hàng"},
    {"type": "guard", "name": "Bác Tổ Trưởng Dân Phố"},
    {"type": "waste_basket", "name": "Thùng Rác Công Cộng"}
  ],
  "obstacles": ["Bẫy ớt bột xua chó", "Vỏ sầu riêng/gạch chắn"],
  "gameTheoryNote": "Bình luận 1 câu ngắn về ma trận quyết định tại địa bàn này"
}
Yêu cầu:
- density: số thực từ 0.3 đến 1.0.
- initialParanoia: số nguyên từ 10 đến 60.
- Chỉ trả về JSON nguyên bản, không dùng markdown, không viết codeblock backtick.`;

      const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.8-flash:generateContent?key=${AiMapGenerator.apiKey}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: prompt }] }],
          generationConfig: {
            temperature: 0.7,
            maxOutputTokens: 500,
          },
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const text: string = data.candidates?.[0]?.content?.parts?.[0]?.text || '';
        const cleanJson = text.replace(/```json/gi, '').replace(/```/g, '').trim();
        const parsed = JSON.parse(cleanJson);

        return {
          locationName: parsed.locationName || locationName.split(',')[0] || 'Khu Phố Mới',
          region,
          density: Math.min(1.0, Math.max(0.2, Number(parsed.density) || 0.6)),
          initialParanoia: Math.min(80, Math.max(10, Number(parsed.initialParanoia) || 20)),
          neighborhoodType: parsed.neighborhoodType || 'Ngõ Phố Đi Bộ',
          landmarks: Array.isArray(parsed.landmarks) ? parsed.landmarks : [],
          obstacles: Array.isArray(parsed.obstacles) ? parsed.obstacles : ['Bẫy ớt bột'],
          gameTheoryNote:
            parsed.gameTheoryNote ||
            'Không gian mở nhưng tầm nhìn giám sát đan xen, hãy cân nhắc chi phí dọn dẹp!',
          isAiGenerated: true,
        };
      }
    } catch {
      // Hết quota, timeout hoặc offline -> Chuyển sang fallback procedural
    }

    return AiMapGenerator.generateFallbackConditions(locationName, region);
  }

  /**
   * Fallback procedural thông minh theo 3 miền & catalog có sẵn
   */
  public static generateFallbackConditions(
    locationName: string,
    region: 'north' | 'central' | 'south'
  ): GeneratedConditions {
    const shortName = locationName.split(',')[0]?.trim() || 'Khu Phố Vỉa Hè';
    const randOffset = (Math.random() - 0.5) * 15; // Random nhẹ paranoia

    if (region === 'north') {
      return {
        locationName: shortName,
        region: 'north',
        density: 0.75,
        initialParanoia: Math.round(25 + randOffset),
        neighborhoodType: 'Ngõ Bàn Cờ — Quán Nước Điểm Hẹn & Bác Dân Phòng',
        landmarks: [
          { type: 'tea_stall', name: 'Quán Trà Đá Thuốc Lào Cô Hoa' },
          { type: 'camera', name: 'Camera Tiệm Vàng Kim Tài 4K' },
          { type: 'guard', name: 'Bác Tổ Trưởng Tuần Tra' },
          { type: 'waste_basket', name: 'Thùng Rác Đầu Ngõ' },
        ],
        obstacles: ['Bẫy Ớt Bột Nhà Số 12', 'Bàn Cờ Tướng Vỉa Hè'],
        gameTheoryNote:
          'Ngõ Bắc hẹp với radar quán nước 360 độ: Rủi ro bị phát hiện khi Chuồn Lẹ đạt 100%!',
        isAiGenerated: false,
      };
    }

    if (region === 'central') {
      return {
        locationName: shortName,
        region: 'central',
        density: 0.6,
        initialParanoia: Math.round(18 + randOffset),
        neighborhoodType: 'Đường Phố Ven Biển / Phố Cổ Thoáng Đãng',
        landmarks: [
          { type: 'tea_stall', name: 'Quầy Nước Mía Bờ Sông' },
          { type: 'camera', name: 'Camera Homestay Khách Du Lịch' },
          { type: 'guard', name: 'Đội Trật Tự Đô Thị Du Lịch' },
          { type: 'waste_basket', name: 'Thùng Rác Xanh Phố Đi Bộ' },
        ],
        obstacles: ['Vỏ Dừa Chắn Vỉa Hè', 'Khách Đi Bộ Tản Mạn'],
        gameTheoryNote:
          'Phố biển thông thoáng nhưng du khách qua lại ngẫu nhiên: Trò Chơi Con Gà dễ nổ ra!',
        isAiGenerated: false,
      };
    }

    // Miền Nam
    return {
      locationName: shortName,
      region: 'south',
      density: 0.85,
      initialParanoia: Math.round(30 + randOffset),
      neighborhoodType: 'Hẻm Nhậu Sầm Uất & Khu Căn Hộ Hiện Đại',
      landmarks: [
        { type: 'tea_stall', name: 'Cà Phê Bệt Vỉa Hè Cô Bảy' },
        { type: 'camera', name: 'Camera Shop Tiện Lợi 24/7' },
        { type: 'guard', name: 'Bảo Vệ Tòa Nhà / Dân Phòng Phường' },
        { type: 'waste_basket', name: 'Thùng Rác Phân Loại' },
      ],
      obstacles: ['Bàn Nhậu Lấn Chiếm', 'Vỏ Trái Cây / Bãi Rác Tạm'],
      gameTheoryNote:
        'Hẻm Sài Gòn đông đúc khiến chi phí ngoại tác bộc phát ngay lập tức khi chuồn lẹ!',
      isAiGenerated: false,
    };
  }
}
